import React, { useEffect } from "react";
import {useSelector} from "react-redux";
import {BrowserRouter as Router, Routes, Route, Link, Navigate, useParams, useSearchParams, useNavigate, useLocation} from "react-router-dom";
//import {createBrowserHistory} from "history";
import {ThemeProvider} from "@mui/material/styles";
import './App.css';
import {darkTheme, lightTheme} from "./themes/default";
import {translations} from "./translations";
import {IntlProvider} from "react-intl";
import {routes, AppRouteType} from "./AppRoutes";
import {AuthProvider, useAuth} from "./hooks/useAuth";
import {UserProvider} from "./hooks/useUser";
import {SnackbarProvider, closeSnackbar} from 'notistack';
import {IconButton} from '@mui/material';
import {CloseRounded} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

 //const history = createBrowserHistory();

//set start and end of week to Monday and Sunday
dayjs.locale('en-gb');

interface Icon {
  href: string;
  rel: string;
  type: string;
  sizes: string;
}

function App() {

//  let theme = darkTheme;
  let theme = lightTheme;
  const messages = translations["en-US"].messages;

//DYNAMICALLY LOAD TAB TITLE AND FAVICON
  useEffect(() => {
    //TODO: ACTUALLY FETCH DYNAMIC STUFF FROM BACKEND
    applyTenant('network_circle')
  }, []);

  const setIconLink = (icon: Icon) => {
    const rel = icon.rel;
    const sizes = icon.sizes;
    let link = document.querySelector(`link[rel="${rel}"][sizes="${sizes}"]`) as HTMLLinkElement | null;
    if(!link){
      link = document.createElement("link");
      link.rel = rel;
      link.setAttribute("sizes", icon.sizes);
      link.setAttribute("type", icon.type);
      document.head.appendChild(link);
    }
    link.href = icon.href;
  }
  const setMetaTag = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if(!meta) {
      meta = document.createElement("meta");
      meta.name = name;
      document.head.appendChild(meta)
    }
    meta.content = content;
  }
  const setManifest = (id: string, href: string) => {
    let link = document.querySelector(`link[rel="manifest"]`) as HTMLLinkElement | null;
    if(!link){
      link = document.createElement("link");
      link.rel = "manifest";
      link.id = id;
    }
    link.href = href;
  }
  const setTitle = (title: string) => {
    document.title="Network Circle - CRM";
  }
  const applyTenant = (tenant: string) => {
    const iconA: Icon = {rel:'icon',
                         type:'image/png',
                         sizes:'16x16',
                         href:'/network_circle/nc-logo-black-16.png'}
    const iconB: Icon = {rel:'icon',
                         type:'image/png',
                         sizes:'32x32',
                         href:'/network_circle/nc-logo-black-32.png'}
    const iconC: Icon = {rel:'icon',
                         type:'image/png',
                         sizes:'48x48',
                         href:'/network_circle/nc-logo-black-48.png'}
    const iconD: Icon = {rel:'apple-touch-icon',
                         type:'image/png',
                         sizes:'180x180',
                         href:'/network_circle/nc-logo-black-180.png'}
    setIconLink(iconA)
    setIconLink(iconB)
    setIconLink(iconC)
    setIconLink(iconD)

    //SET TITLE
    setTitle('Network Circle - CRM')

    //SET META DATA
    setMetaTag('description', 'The Network Circle Customer Relationship Management Web Application')

    //TODO: FETCH CORRECT MANIFEST
    setManifest('nc-manifest', '/network_circle/nc-manifest.json')

    //

  }


////////////////////////////////////


  const ProtectedRoute = ({children}: {children: any}) => {
    //navigate to home when not properly authenticated
    const {accessToken}   = useAuth();
    const location = useLocation();

    if(!accessToken) {
      return <Navigate to="/login" replace state={{from: location}} />
    }

    return children;
  }


  const renderRoute = (route: AppRouteType, key: string): React.ReactNode => {
    //render route recursively
    const ThisComponent = route.component;

    //index routes never have children
    if(route.type === 'public' && route.index){
      return (
        <Route
          key={key}
          index
          element={<ThisComponent/>}
        />
      )
    } else if(route.type === 'protected' && route.index){
      return (
        <Route
          key={key}
          index
          element={<ProtectedRoute>
                     <ThisComponent />
                   </ProtectedRoute>}
        />
      )
    } else if(route.type === 'public'){
        return (
          <Route
            key={key}
            path={route.path}
            element={<ThisComponent/>}
          >
            {route.children.map((childRoute, index) =>
              renderRoute(childRoute, `${key}-${index}`)
            )}
          </Route>
        )
      } else if(route.type === 'protected'){
        return (
          <Route
            key={key}
            path={route.path}
            element={<ProtectedRoute>
                       <ThisComponent />
                     </ProtectedRoute>
                   }
          >
            {route.children.map((childRoute, index) => {
               let newKey = '';
               if(childRoute.index) newKey = `${key}-index-${index}`;
               if(childRoute.path) newKey = `${key}-${childRoute.path}`;
               return renderRoute(childRoute, newKey)
             })}
          </Route>
        )
      }
    }

  ////////////////////////////////////

  return (
    <div className="App">
    <IntlProvider
      messages={messages}
      locale={"en-GB"}
      defaultLocale="en-GB"
      onError={() => undefined}
    >
      <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={"en-gb"}
      >
      <ThemeProvider theme={theme}>
        <SnackbarProvider
          maxSnack={3}
          autoHideDuration={2000}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          action={(snackbarId) => (
            <IconButton
              size="small"
              color="inherit"
              onClick={()=> closeSnackbar(snackbarId)}
            >
              <CloseRounded fontSize="small"/>
            </IconButton>
          )}
        >
        <Router>
          <AuthProvider>
          <UserProvider>
          <Routes>
             {routes.map((route, index) => {
               let key = '';
               if(route.index) key = `index-${index}`;
               if(route.path) key = `${route.path}`;
               return renderRoute(route, key)
             })}
            </Routes>
            </UserProvider>
          </AuthProvider>
        </Router>
        </SnackbarProvider>
      </ThemeProvider>
      </LocalizationProvider>
    </IntlProvider>
    </div>
  );
}

export default App;
